// config
import { VERSION } from 'config';
// modules
import Logger from 'modules/Logger';
import UserService from '../../../User/service';
// consts
import {
  STORAGE_KEYS_CROSS_USERS,
  STORAGE_KEY_ALL_USERS,
  CLEAR_REQUIRED_KEYS,
} from './constants';

class StorageService {
  storage: Storage;

  constructor(type: StorageType) {
    this.storage = type === 'session' ? sessionStorage : localStorage;
  }

  private get lastClearedVersion() {
    return this.getItem<string>('LAST_CLEARED_VERSION') || '0';
  }

  private get currentVersion() {
    return VERSION.replace('-SNAPSHOT', '');
  }

  private get profileId() {
    return UserService.profileId;
  }

  private isUpdateNeeded = (
    curVersion: string,
    lastVersion: string,
  ): boolean => {
    const [major1 = '0', minor1 = '0', patch1 = '0'] = curVersion.split('.');
    const [major2 = '0', minor2 = '0', patch2 = '0'] = lastVersion.split('.');

    if (+major1 > +major2) return true;
    if (+major1 < +major2) return false;

    // major parts are equal
    if (+minor1 > +minor2) return true;
    if (+minor1 < +minor2) return false;

    // minor parts are equal
    if (+patch1 > +patch2) return true;

    return false;
  };

  public checkKeysForClearing = () => {
    const { lastClearedVersion, currentVersion } = this;

    if (this.isUpdateNeeded(currentVersion, lastClearedVersion)) {
      // make clearing for all previous versions upto lastClearedVersion
      const clearKeys = CLEAR_REQUIRED_KEYS.reverse();

      for (let i = 0; i < clearKeys.length; i++) {
        const { version, keys } = clearKeys[i];

        if (this.isUpdateNeeded(version, lastClearedVersion)) {
          keys.forEach(key => this.removeItem(key));
        } else {
          break;
        }
      }

      this.setItem('LAST_CLEARED_VERSION', currentVersion);
    }
  };

  public getItem = <T = string>(key: StorageKey): T | null => {
    const profileId = STORAGE_KEYS_CROSS_USERS.includes(key)
      ? STORAGE_KEY_ALL_USERS
      : this.profileId;

    if (!profileId) return null;

    const dataAllUsers = this.storage.getItem(key);

    if (!dataAllUsers) return null;

    try {
      const dataAllUsersParsed: WebStorageDataType<T> = JSON.parse(
        dataAllUsers,
      );

      const userData = dataAllUsersParsed[profileId];

      return userData || null;
    } catch (error) {
      Logger.error(`key: ${key}`, error);

      // we need to clear data for this key as we changed data format as object with profileId as keys
      this.removeItem(key);

      return null;
    }
  };

  public setItem = <T = string>(key: StorageKey, data: T) => {
    try {
      const profileId = STORAGE_KEYS_CROSS_USERS.includes(key)
      ? STORAGE_KEY_ALL_USERS
      : this.profileId;

    if (!profileId) return;

    const dataAllUsers = this.storage.getItem(key) || '{}';

    let dataAllUsersParsed: WebStorageDataType<T> = {};

    try {
      dataAllUsersParsed = JSON.parse(dataAllUsers);
    } catch (err) {
      // dataAllUsers is not an object
      dataAllUsersParsed = {};
    }

    dataAllUsersParsed = {
      ...dataAllUsersParsed,
      [profileId]: data,
    };

    this.storage.setItem(key, JSON.stringify(dataAllUsersParsed));
    } catch (e) {
      Logger.log('Storage', e);
    }
  };

  public removeItem = (key: StorageKey) => {
    this.storage.removeItem(key);
  };

  // sync data between storage and observable
  public sync = (key: StorageKey, newValue: unknown) => {
    // accepts only null as value to remove data from storage
    // undefined means property isn't initialized
    if (newValue === undefined) return;

    // null means remove items
    if (newValue === null) {
      return this.removeItem(key);
    }

    // new value
    this.setItem(key, newValue);
  };
}

export const SStorage = new StorageService('session');
export const LStorage = new StorageService('local');
